import {TagColor} from '@benchling/nucleus-ui';

import {
  ExtensionType,
  ExtensionLabelPlacement,
  ExtensionLabelKey,
  ScientificModelCategory,
} from 'graphql-client/__generated/graphqlGlobalTypes';

import type {ListExtensions} from '../__generated/ListExtensions';

export type Extension = ListExtensions['extensionRegistryListing']['items'][number];

export type ExtensionLabelSpec = ListExtensions['extensionLabelSpecs'][number];

export function registryCatalogKeysForPlacement(
  specs: ReadonlyArray<ExtensionLabelSpec> | null | undefined,
  placement: ExtensionLabelPlacement
): ReadonlySet<string> | undefined {
  if (specs == null || specs.length === 0) {
    return undefined;
  }
  return new Set(specs.filter((s) => s.placement === placement).map((s) => String(s.key)));
}

/** Catalog / GraphQL label rows store ``key`` as the same string as ``ExtensionLabelKey``. */
export function findExtensionLabelSpecByCatalogKey(
  specs: ReadonlyArray<ExtensionLabelSpec> | null | undefined,
  catalogKey: string
): ExtensionLabelSpec | undefined {
  return specs?.find((s) => String(s.key) === catalogKey);
}

export type RegistryLabel = NonNullable<Extension['labels']>[number];

const EXTENSION_TYPE_DISPLAY: Record<ExtensionType, {singular: string; plural: string}> = {
  [ExtensionType.AGENT_SKILL]: {singular: 'Agent Skill', plural: 'Agent Skills'},
  [ExtensionType.MCP_SERVER]: {singular: 'AI Connector', plural: 'AI Connectors'},
  [ExtensionType.MODEL]: {singular: 'Scientific Model', plural: 'Scientific Models'},
};

function pickExtensionTypeField(field: 'singular' | 'plural'): Record<ExtensionType, string> {
  return Object.fromEntries(
    (
      Object.entries(EXTENSION_TYPE_DISPLAY) as Array<[ExtensionType, {singular: string; plural: string}]>
    ).map(([extensionType, labels]) => [extensionType, labels[field]])
  ) as Record<ExtensionType, string>;
}

export const EXTENSION_TYPE_LABELS = pickExtensionTypeField('singular');

export const EXTENSION_TYPE_LABELS_PLURAL = pickExtensionTypeField('plural');

export const MODEL_CATEGORY_COLORS: Record<ScientificModelCategory, TagColor> = {
  [ScientificModelCategory.STRUCTURE_PREDICTION]: TagColor.TEAL,
  [ScientificModelCategory.GENERATIVE]: TagColor.GRAPE,
};

/** Section title for a filter section — uses ``displayName`` from ``extensionLabelSpecs``. */
export function formatRegistryLabelKey(
  catalogKey: string,
  labelSpecs: ReadonlyArray<ExtensionLabelSpec>
): string {
  const spec = findExtensionLabelSpecByCatalogKey(labelSpecs, catalogKey);
  return spec != null ? spec.displayName : catalogKey;
}

export function tagColorForRegistryLabel(lab: Pick<RegistryLabel, 'key' | 'value'>): TagColor {
  if (lab.key !== ExtensionLabelKey.CATEGORY) {
    return TagColor.DEFAULT;
  }
  const color = MODEL_CATEGORY_COLORS[lab.value as ScientificModelCategory];
  return color ?? TagColor.DEFAULT;
}

/**
 * Tags beside the title on listing cards / detail header — uses ``extensionLabelSpecs`` placement
 * (content store and installed-extensions queries both load specs).
 */
export function registryLabelsForExtensionHeader(
  labels: ReadonlyArray<RegistryLabel> | null | undefined,
  labelSpecs: ReadonlyArray<ExtensionLabelSpec>
): ReadonlyArray<RegistryLabel> {
  const list = labels ?? [];
  const headerKeys = registryCatalogKeysForPlacement(labelSpecs, ExtensionLabelPlacement.CARD_HEADER);
  if (headerKeys == null || headerKeys.size === 0) {
    return [];
  }
  return list.filter((lab) => headerKeys.has(String(lab.key)));
}

export function registryLabelsModelInputTypes(
  labels: ReadonlyArray<RegistryLabel> | null | undefined
): ReadonlyArray<RegistryLabel> {
  return (labels ?? []).filter((lab) => lab.key === ExtensionLabelKey.INPUT_TYPE);
}

/**
 * Version tags that can be assigned to an extension version.
 * Must match the backend enum (AIModelVersionTag in ai_model_version_tags.py).
 */
export enum ModelVersionTag {
  DEFAULT = 'DEFAULT',
}

export const VERSION_TAGS_BY_EXTENSION_TYPE: Partial<Record<ExtensionType, Array<string>>> = {
  [ExtensionType.MODEL]: Object.values(ModelVersionTag),
};
